import { Link } from "react-router-dom";
import { Button, Card, Container, Icon } from "@wiseowl/design-system";

/*
 * /how-it-works — a short, public explainer of the reflection loop.
 * Renders inside MarketingLayout (header + footer). Brand voice: calm,
 * sentence case, no exclamation marks.
 */
const STEPS = [
  {
    icon: "spark" as const,
    title: "Arrive and breathe",
    body: "A few slow breaths to land. No timer racing you — just a moment to feel the chair beneath you.",
  },
  {
    icon: "book" as const,
    title: "Notice and write",
    body: "Pick the word for how you're arriving, then write what feels heaviest. Nobody is reading over your shoulder.",
  },
  {
    icon: "chat" as const,
    title: "Hear one quiet thought",
    body: "We reflect back what we noticed — a gentle insight, a question to sit with, one tiny action for today.",
  },
];

export const HowItWorks = () => (
  <>
    <section className="border-b border-line">
      <Container className="py-12 md:py-16">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-pill border border-line bg-paper px-3 py-1.5 font-mono text-eyebrow font-semibold uppercase tracking-wider text-teal">
            <Icon name="spark" size={14} className="text-mustard" />
            About 90 seconds
          </div>
          <h1 className="font-serif text-[clamp(28px,4.5vw,44px)] font-medium leading-[1.08] tracking-tight text-ink text-pretty">
            How the reset works
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-muted text-pretty">
            Three small steps, the same each day. Nothing to crush, nothing to win — just a quiet pause that reflects you back to yourself.
          </p>
        </div>

        <div className="mx-auto mt-10 grid max-w-4xl gap-4 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <Card key={s.title}>
              <div className="flex items-center gap-2.5">
                <div className="grid h-10 w-10 place-items-center rounded-md bg-teal/[0.08] text-teal">
                  <Icon name={s.icon} size={22} />
                </div>
                <span className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-muted">
                  Step {i + 1}
                </span>
              </div>
              <h3 className="mt-3 font-serif text-h3 font-medium text-ink">{s.title}</h3>
              <p className="mt-1.5 text-small text-muted">{s.body}</p>
            </Card>
          ))}
        </div>

        <div className="mx-auto mt-10 max-w-2xl text-center">
          <p className="font-serif text-h3 italic leading-relaxed text-ink-soft text-pretty">
            Your reflection stays on your device until you choose to save it. Sign up to keep your words and build a personal mood bank.
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/">
              <Button size="lg">Begin today&apos;s reset</Button>
            </Link>
            <Link to="/sign-up">
              <Button variant="ghost" size="lg">Create an account</Button>
            </Link>
          </div>
        </div>
      </Container>
    </section>
  </>
);
